import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, CalendarDays, Users, Compass } from "lucide-react";
import { DESTINATIONS, EXPERIENCE_TYPES } from "../data/content";
import { useEnquiry } from "./EnquiryContext";

const Field = ({ icon: Icon, label, children }) => (
  <label className="flex flex-col gap-2 px-5 py-4 border-b lg:border-b-0 lg:border-r border-gold/10 last:border-r-0">
    <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold/80">
      <Icon size={12} /> {label}
    </span>
    {children}
  </label>
);

const inputCls =
  "bg-transparent text-sm text-cream placeholder:text-fog focus:outline-none w-full [color-scheme:dark]";

const BookingBarInner = () => {
  const { openEnquiry } = useEnquiry();
  const [form, setForm] = useState({ destination: "", date: "", travellers: "", experience: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div className="glass-panel shadow-2xl shadow-black/60" data-testid="booking-bar">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_1fr_auto]">
        <Field icon={MapPin} label="Where do you want to go?">
          <select
            data-testid="booking-bar-destination-select"
            value={form.destination}
            onChange={set("destination")}
            className={inputCls}
          >
            <option value="" className="bg-forest-900">Anywhere wild</option>
            {DESTINATIONS.map((d) => (
              <option key={d.name} value={d.name} className="bg-forest-900">{d.name}</option>
            ))}
          </select>
        </Field>
        <Field icon={CalendarDays} label="Travel Date">
          <input
            data-testid="booking-bar-date-input"
            type="date"
            value={form.date}
            onChange={set("date")}
            className={inputCls}
          />
        </Field>
        <Field icon={Users} label="Travellers">
          <select
            data-testid="booking-bar-travellers-select"
            value={form.travellers}
            onChange={set("travellers")}
            className={inputCls}
          >
            <option value="" className="bg-forest-900">Select</option>
            {["1–2", "3–4", "5–8", "9+"].map((t) => (
              <option key={t} value={t} className="bg-forest-900">{t}</option>
            ))}
          </select>
        </Field>
        <Field icon={Compass} label="Experience">
          <select
            data-testid="booking-bar-experience-select"
            value={form.experience}
            onChange={set("experience")}
            className={inputCls}
          >
            <option value="" className="bg-forest-900">Choose</option>
            {EXPERIENCE_TYPES.map((t) => (
              <option key={t} value={t} className="bg-forest-900">{t}</option>
            ))}
          </select>
        </Field>
        <button
          data-testid="booking-bar-plan-btn"
          onClick={() =>
            openEnquiry({
              destination: form.destination,
              travel_date: form.date,
              travellers: form.travellers,
              experience: form.experience,
            })
          }
          className="bg-gold text-forest-950 font-mono text-xs uppercase tracking-[0.22em] px-10 py-5 lg:py-0 hover:bg-cream transition-colors duration-300 whitespace-nowrap"
        >
          Plan My Trip
        </button>
      </div>
    </div>
  );
};

export const BookingBar = () => (
  <div className="relative z-20 max-w-6xl mx-auto px-5 sm:px-8 -mt-16 lg:-mt-20">
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
    >
      <BookingBarInner />
    </motion.div>
  </div>
);
